/*
 * The graph tab: the force layout plus the reader it opens into.
 *
 * Clicking a node does two things at once — dims everything outside its
 * neighbourhood and opens the rendered page beside the graph. Focus without inline
 * reading means clicking a node tells you nothing; reading without focus means you
 * cannot see what the page is connected to. Both together is what makes the view
 * worth having.
 */
import { useMemo, useState } from 'react'
import {
  getWikiContent,
  type LinkGraph,
  type WikiContent,
  type WikiIndex,
} from '../../../api/knowledgeBases.ts'
import { AsyncState, Empty, Loaded } from '../components/AsyncState.tsx'
import { ForceGraph } from '../components/ForceGraph.tsx'
import { Markdown, resolveTarget } from '../components/Markdown.tsx'
import { baseName, num } from '../lib/format.ts'
import { useResource, type Resource } from '../lib/useResource.ts'

interface GraphTabProps {
  kbId: string
  links: Resource<LinkGraph>
  wiki: Resource<WikiIndex>
}

export function GraphTab({ kbId, links, wiki }: GraphTabProps) {
  const [selected, setSelected] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const known = useMemo(
    () => new Set((wiki.data?.pages ?? []).map((page) => page.path)),
    [wiki.data],
  )

  const page = useResource<WikiContent>(`${kbId}:graph-page:${selected ?? 'none'}`, () =>
    selected ? getWikiContent(kbId, selected) : Promise.reject(new Error('страница не выбрана')),
  )

  return (
    <div className="pane">
      <Loaded resource={links} what="граф ссылок" skeletonRows={5}>
        {(data) =>
          (data.pages ?? []).length === 0 ? (
            <Empty title="Ссылок пока нет">
              Граф строится по <code>[[wikilink]]</code>-ссылкам между страницами. После первого
              ingest здесь появятся хабы и висяки.
            </Empty>
          ) : (
            <div className="graph-split">
              <div>
                <div className="graph">
                  <div className="graph__legend">
                    <span>
                      <i className="graph__dot" style={{ background: '#c2643f' }} /> концепты
                    </span>
                    <span>
                      <i className="graph__dot" style={{ background: '#6d9ad4' }} /> сущности
                    </span>
                    <span>
                      <i className="graph__dot" style={{ background: '#5c5c62' }} /> источники
                    </span>
                    <span>
                      <i className="graph__dot" style={{ background: '#34d399' }} /> синтез
                    </span>
                    <span>
                      <i
                        className="graph__dot"
                        style={{ background: 'transparent', border: '1.5px dashed #f87171' }}
                      />{' '}
                      сирота — нет входящих ссылок
                    </span>
                    <span style={{ marginLeft: 'auto', color: 'var(--mu2)' }}>
                      размер узла = число входящих ссылок
                    </span>
                  </div>
                  <ForceGraph
                    links={data}
                    selected={selected}
                    onSelect={setSelected}
                    query={query}
                  />
                </div>

                <div className="filters" style={{ marginTop: 12 }}>
                  <input
                    className="input"
                    value={query}
                    placeholder="Подсветить по имени страницы"
                    onChange={(event) => setQuery(event.target.value)}
                    style={{ minWidth: 240 }}
                  />
                  {selected ? (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setSelected(null)}
                    >
                      Снять фокус
                    </button>
                  ) : null}
                  <span className="muted-2" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
                    {num((data.pages ?? []).length)} страниц ·{' '}
                    {num((data.orphans ?? []).length)} сирот ·{' '}
                    {num((data.broken ?? []).length)} битых ссылок
                  </span>
                </div>

                {(data.orphans ?? []).length > 0 || (data.broken ?? []).length > 0 ? (
                  <div className="callout" style={{ marginTop: 12 }}>
                    <b>Висяки и битые ссылки — это и есть то, что предложит lint.</b>{' '}
                    {(data.orphans ?? []).length > 0 ? (
                      <>
                        Сироты ({num((data.orphans ?? []).length)}):{' '}
                        {(data.orphans ?? []).slice(0, 6).map(baseName).join(', ')}
                        {(data.orphans ?? []).length > 6 ? ' …' : ''}.{' '}
                      </>
                    ) : null}
                    {(data.broken ?? []).length > 0 ? (
                      <>
                        {/* A broken link is a pair: the page it sits in and the
                            target that does not resolve — both halves are what
                            makes the row actionable. */}
                        Битые ссылки ({num((data.broken ?? []).length)}):{' '}
                        {(data.broken ?? [])
                          .slice(0, 6)
                          .map((link) => `${baseName(link.source)} → ${link.target}`)
                          .join(', ')}
                        {(data.broken ?? []).length > 6 ? ' …' : ''}.
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="card">
                <div className="card__head">
                  <h3>{selected ? baseName(selected) : 'Читалка'}</h3>
                  <div className="right">
                    {selected ? <span className="path">{selected}</span> : null}
                  </div>
                </div>
                {selected === null ? (
                  <Empty title="Выберите узел">
                    Клик по узлу гасит всё вне его окружения и открывает страницу здесь.
                  </Empty>
                ) : page.status !== 'ready' || page.data === null ? (
                  <AsyncState
                    what="страницу"
                    status={page.status}
                    error={page.error}
                    httpStatus={page.httpStatus}
                    onRetry={page.reload}
                    skeletonRows={5}
                  />
                ) : (
                  <div className="viewer">
                    <Markdown
                      content={page.data.content}
                      known={known}
                      onOpenLink={(target) => {
                        const resolved = resolveTarget(target, known)
                        if (resolved) setSelected(resolved)
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        }
      </Loaded>
    </div>
  )
}
